import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./user.scss";
import {
  fetchUsers,
  addNewUser,
  editUser,
} from "../../store/slices/User/thunk";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import EditOutlined from "@mui/icons-material/EditOutlined";
import SearchIcon from "@mui/icons-material/Search";
import PersonOutline from "@mui/icons-material/PersonOutline";
import { useNavigate } from "react-router-dom";
import Loader from "../../components/loader";
import AddIcon from "@mui/icons-material/Add";
import { toast, ToastContainer } from "react-toastify";
import { useClientFilter } from "../../Hooks/useClientFilter";
import FilterPopover from "../../components/FilterPopover/FilterPopover";

const UsersTable = () => {
  const [search, setSearch] = useState("");
  const dispatch: any = useDispatch();
  const { users, loading, error } = useSelector((state: any) => state.user);
  const [getAllUser, setAllUser] = useState([]);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const DEBOUNCE_DELAY = 500; // milliseconds
  const ITEMS_PER_PAGE = 10;
  const pageCount = Math.ceil(totalCount / ITEMS_PER_PAGE);
  const [isLoading, setIsLoading] = useState(false);
  const [getCurrentUser, setCurrentUser] = useState<any>(null);

  const { filteredData, filters, setFilter, clearFilter } = useClientFilter(getAllUser);

  useEffect(() => {
    const fetchUser = async () => {
      const userData = localStorage.getItem("user");
      if (userData) {
        setCurrentUser(JSON.parse(userData));
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const handler = setTimeout(async () => {
      setIsLoading(true);
      try {
        const response = await dispatch(
          fetchUsers(page, ITEMS_PER_PAGE, search)
        );
        if (
          response &&
          response?.data &&
          response?.data?.data &&
          response?.data?.data?.length &&
          response?.isSuccess
        ) {
          setAllUser(response?.data?.data);
          setTotalCount(response?.data?.totalCount);
        } else {
          setAllUser([]);
          setTotalCount(0);
        }
      } catch (error) {
        console.log(getAllUser);
        setAllUser([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    }, DEBOUNCE_DELAY);
    return () => {
      clearTimeout(handler);
    };
  }, [search, page, dispatch]);

  const gotoEdit = (id: string) => {
    navigate(`/editUser/${id}`);
  };
  const navigateToedituser = () => {
    navigate("/addUser");
  };

  const handleCopy = async (user: any) => {
    if (!user?.whatsappLink) return;
    try {
      const url = new URL(user.whatsappLink);
      const text = url.searchParams.get("text");

      if (text) {
        await navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard!");
      } else {
        toast.success("No text found in link!");
      }
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const getRoleDisplay = (role: string) => {
    if (role === "admin") return "Administrator";
    if (role === "schoolAdmin") return "Staff Member";
    return role;
  };

  return (
    <div style={{ backgroundColor: "transparent", overflow: "hidden", fontFamily: "'Inter', sans-serif", display: "flex", flexDirection: "column", height: "100%" }}>
      <ToastContainer />

      {/* Top Header Layout */}
      <div className="d-flex justify-content-between align-items-center mb-4" style={{ flexShrink: 0 }}>
        <div className="d-flex flex-column">
          <h2 style={{ color: "#0f172a", fontWeight: "700", fontSize: "24px", margin: "0", fontFamily: "sans-serif" }}>
            User List
          </h2>
          <p style={{ color: "#64748b", fontSize: "14px", margin: "4px 0 0 0", fontWeight: "400" }}>
            Manage all system users
          </p>
        </div>
        {getCurrentUser && getCurrentUser.role !== 'superAdmin' && (
        <button
          onClick={navigateToedituser}
          style={{
            background: "#2563EB",
            color: "#fff",
            borderRadius: "8px",
            padding: "10px 18px",
            fontWeight: "600",
            fontSize: "14px",
            border: "none",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            cursor: "pointer"
          }}
        >
          <AddIcon style={{ fontSize: "18px" }} />
          Add New User
        </button>
        )}
      </div>

      {/* Main Table White Card Frame */}
      <Paper elevation={0} sx={{ padding: "0px", borderRadius: "12px", border: "1px solid #E5E7EB", backgroundColor: "#ffffff", overflow: "hidden", display: "flex", flexDirection: "column", flex: 1 }}>
        
        {/* Search Input Box Area */}
        <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div className="p-4" style={{ borderBottom: "1px solid #F3F4F6", flexShrink: 0 }}>
          <div className="position-relative" style={{ maxWidth: "320px" }}>
            <SearchIcon style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#9CA3AF", fontSize: "20px" }} />
            <input
              style={{
                border: "1px solid #D1D5DB",
                outline: "none",
                borderRadius: "6px",
                width: "100%",
                padding: "9px 12px 9px 40px",
                fontSize: "14px",
                color: "#1F2937",
                backgroundColor: "#ffffff"
              }}
              placeholder="Search by name or email..."
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Table Layout */}
        <TableContainer sx={{ flex: 1, overflow: "auto",padding:'0px 24px' }}>
          <Table sx={{ minWidth: 650 }} stickyHeader>
            <TableHead>
              <TableRow style={{ backgroundColor: "#F9FAFB" }}>
                <TableCell style={{ color: "#6B7280", fontWeight: "600", fontSize: "13px", borderBottom: "1px solid #E5E7EB", padding: "12px 16px", width: "60px" }}>S.No</TableCell>
                <TableCell style={{ color: "#6B7280", fontWeight: "600", fontSize: "13px", borderBottom: "1px solid #E5E7EB", padding: "12px 16px" }}>Name<FilterPopover column="name" filterType="text" currentValue={filters.name || ""} onFilterChange={setFilter} onClear={clearFilter} /></TableCell>
                <TableCell style={{ color: "#6B7280", fontWeight: "600", fontSize: "13px", borderBottom: "1px solid #E5E7EB", padding: "12px 16px" }}>Email<FilterPopover column="email" filterType="text" currentValue={filters.email || ""} onFilterChange={setFilter} onClear={clearFilter} /></TableCell>
                <TableCell style={{ color: "#6B7280", fontWeight: "600", fontSize: "13px", borderBottom: "1px solid #E5E7EB", padding: "12px 16px" }}>Role<FilterPopover column="role" filterType="text" currentValue={filters.role || ""} onFilterChange={setFilter} onClear={clearFilter} /></TableCell>
                <TableCell style={{ color: "#6B7280", fontWeight: "600", fontSize: "13px", borderBottom: "1px solid #E5E7EB", padding: "12px 16px", textAlign: "right" }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" style={{ padding: "40px" }}>
                  <Loader />
                </TableCell>
              </TableRow>
            ) : (
              <>
                {filteredData.map((user: any, index) => (
                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#F9FAFB' } }}>
                    <TableCell style={{ color: "#374151", fontWeight: "500", fontSize: "14px", borderBottom: "1px solid #F3F4F6", padding: "14px 16px" }}>
                      {(page - 1) * ITEMS_PER_PAGE + index + 1}
                    </TableCell>
                    <TableCell style={{ borderBottom: "1px solid #F3F4F6", padding: "14px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div style={{ width: "36px", height: "36px", borderRadius: "50%", backgroundColor: "#E0E7FF", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "600", fontSize: "14px", color: "#4F46E5", overflow: "hidden", flexShrink: 0 }}>
                          {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                        </div>
                        <span style={{ color: "#111827", fontWeight: "600", fontSize: "14px" }}>{user.name || "--"}</span>
                      </div>
                    </TableCell>
                    <TableCell style={{ color: "#6B7280", fontSize: "14px", borderBottom: "1px solid #F3F4F6", padding: "14px 16px" }}>
                      {user.email || "--"}
                    </TableCell>
                    <TableCell style={{ borderBottom: "1px solid #F3F4F6", padding: "14px 16px" }}>
                      <span
                        style={{
                          backgroundColor: "#EFF6FF",
                          border: "none",
                          color: "#2563EB",
                          borderRadius: "9999px",
                          padding: "4px 12px",
                          fontSize: "12px",
                          fontWeight: "600",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <PersonOutline style={{ fontSize: "14px" }} />
                        {getRoleDisplay(user.role)}
                      </span>
                    </TableCell>
                    <TableCell style={{ borderBottom: "1px solid #F3F4F6", padding: "14px 16px", textAlign: "right" }}>
                      {getCurrentUser && getCurrentUser.role !== 'superAdmin' && (
                      <button
                        onClick={() => gotoEdit(user._id)}
                        type="button"
                        style={{
                          border: "none",
                          borderRadius: "50%",
                          color: "#2563EB",
                          backgroundColor: "#EFF6FF",
                          padding: "0",
                          width: "32px",
                          height: "32px",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer"
                        }}
                      >
                        <EditOutlined style={{ fontSize: "16px" }} />
                      </button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {filteredData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" style={{ color: "#6B7280", padding: "40px", fontSize: "14px" }}>
                      No User Found.
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
            </TableBody>
          </Table>
        </TableContainer>
        </div>

        {/* Bottom Footer Information Bar */}
        <div className="d-flex justify-content-between align-items-center p-4" style={{ backgroundColor: "#ffffff", borderTop: "1px solid #F3F4F6" }}>

          {/* Left Text Counter Block */}
          <div style={{ color: "#6B7280", fontSize: "14px", fontWeight: "400" }}>
            Showing <span style={{ fontWeight: "500", color: "#374151" }}>{getAllUser.length > 0 ? (page - 1) * ITEMS_PER_PAGE + 1 : 0}</span> to <span style={{ fontWeight: "500", color: "#374151" }}>{Math.min(page * ITEMS_PER_PAGE, totalCount)}</span> of <span style={{ fontWeight: "500", color: "#374151" }}>{totalCount}</span> results
          </div>

          {/* Centered Numbers Pagination Block */}
          <nav style={{ flexShrink: 0 }}>
            <ul className="pagination justify-content-center" style={{ margin: 0 }}>
              <li className={`page-item ${page === 1 && "disabled"}`}>
                <button className="page-link" onClick={() => setPage((p) => Math.max(p - 1, 1))} style={{ color: "#374151", border: "1px solid #E5E7EB", borderRadius: "6px", margin: "0 2px" }}>&lt;</button>
              </li>
              {Array.from({ length: Math.min(5, pageCount) }, (_, i) => (
                <li key={i + 1} className={`page-item ${page === i + 1 && "active"}`}>
                  <button
                    className="page-link"
                    onClick={() => setPage(i + 1)}
                    style={{
                      color: page === i + 1 ? "#ffffff" : "#374151",
                      backgroundColor: page === i + 1 ? "#2563EB" : "transparent",
                      border: "1px solid #E5E7EB",
                      borderRadius: "6px",
                      margin: "0 2px",
                      fontWeight: page === i + 1 ? "600" : "400",
                    }}
                  >
                    {i + 1}
                  </button>
                </li>
              ))}
              {pageCount > 5 && (
                <>
                  <li className="page-item disabled">
                    <span className="page-link" style={{ color: "#6B7280", border: "1px solid #E5E7EB", borderRadius: "6px", margin: "0 2px", backgroundColor: "transparent" }}>...</span>
                  </li>
                  <li className="page-item">
                    <button className="page-link" onClick={() => setPage(pageCount)} style={{ color: "#374151", border: "1px solid #E5E7EB", borderRadius: "6px", margin: "0 2px" }}>{pageCount}</button>
                  </li>
                </>
              )}
              <li className={`page-item ${page === pageCount && "disabled"}`}>
                <button className="page-link" onClick={() => setPage((p) => Math.min(p + 1, pageCount))} style={{ color: "#374151", border: "1px solid #E5E7EB", borderRadius: "6px", margin: "0 2px" }}>&gt;</button>
              </li>
            </ul>
          </nav>

          {/* Right Selection Menu Dropdown Block */}
          <div className="d-flex align-items-center" style={{ gap: "8px" }}>
            <select
              value={ITEMS_PER_PAGE}
              disabled
              style={{
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                padding: "6px 28px 6px 12px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                backgroundColor: "#ffffff",
                cursor: "default",
                outline: "none",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
              }}
            >
              <option value={ITEMS_PER_PAGE}>{ITEMS_PER_PAGE} / page</option>
            </select>
          </div>
        </div>

      </Paper>
    </div>
  );
};

export default UsersTable;
